import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  UserIcon,
  UsersIcon,
  DocumentChartBarIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  AcademicCapIcon,
  ClipboardDocumentCheckIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { H1, H2, H3, Body, Card, Badge, Button, Spinner } from '../ui';
import { Input, Select, TextArea } from '../forms';
import { Modal } from '../ui/Modal';
import { useChildren, useClassrooms, useLearningAreas } from '../../hooks/api';

interface ReportWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (config: ReportConfig) => void;
  initialType?: string;
}

interface ReportConfig {
  type: 'individual' | 'classroom' | 'assessment-matrix' | 'analytics';
  title: string;
  description: string;
  dateRange: {
    start: string;
    end: string;
  };
  // Individual report specific
  childId?: string;
  // Classroom report specific
  classroomId?: string;
  // Assessment matrix specific
  learningAreaIds?: string[];
  // Analytics specific
  metrics?: string[];
  // Common options
  includeObservations: boolean;
  includeAssessments: boolean;
  includeMedia: boolean;
  includeRecommendations: boolean;
  format: 'pdf' | 'docx' | 'online';
}

const ReportWizard: React.FC<ReportWizardProps> = ({
  isOpen,
  onClose,
  onGenerate,
  initialType = 'individual',
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  
  const [config, setConfig] = useState<ReportConfig>({
    type: initialType as ReportConfig['type'],
    title: '',
    description: '',
    dateRange: {
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
      end: new Date().toISOString().split('T')[0], // today
    },
    includeObservations: true,
    includeAssessments: true,
    includeMedia: false,
    includeRecommendations: true,
    format: 'pdf',
  });
  
  const { data: childrenData } = useChildren();
  const { data: classroomsData } = useClassrooms();
  const { data: learningAreas } = useLearningAreas();
  
  const children = childrenData?.items || [];
  const classrooms = classroomsData || [];
  
  const reportTypes = [
    {
      id: 'individual',
      title: 'Individual Child Report',
      description: 'Comprehensive progress report for a single child',
      icon: UserIcon,
      color: 'bg-blue-500',
    },
    {
      id: 'classroom',
      title: 'Classroom Report',
      description: 'Overview of all children in a classroom',
      icon: UsersIcon,
      color: 'bg-green-500',
    },
    {
      id: 'assessment-matrix',
      title: 'Assessment Matrix',
      description: 'Grid view of assessments across learning areas',
      icon: DocumentChartBarIcon,
      color: 'bg-purple-500',
    },
    {
      id: 'analytics',
      title: 'Analytics Report',
      description: 'Data-driven insights and trends',
      icon: ChartBarIcon,
      color: 'bg-orange-500',
    },
  ];
  
  const availableMetrics = [
    'Average Scores',
    'Progress Trends',
    'Completion Rates',
    'Milestone Achievements',
    'Areas of Concern',
    'Learning Area Comparison',
    'Time-based Analysis',
  ];
  
  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      await onGenerate(config);
      onClose();
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setIsGenerating(false);
    }
  };
  
  const updateConfig = (updates: Partial<ReportConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };
  
  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return config.type;
      case 2:
        if (config.type === 'individual') return config.childId;
        if (config.type === 'classroom') return config.classroomId;
        if (config.type === 'assessment-matrix') return config.learningAreaIds && config.learningAreaIds.length > 0;
        if (config.type === 'analytics') return config.metrics && config.metrics.length > 0;
        return true;
      case 3:
        return config.dateRange.start && config.dateRange.end;
      case 4:
        return config.title.trim().length > 0;
      default:
        return true;
    }
  };
  
  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return 'Choose Report Type';
      case 2: return 'Select Content';
      case 3: return 'Set Date Range';
      case 4: return 'Configure Options';
      default: return 'Generate Report';
    }
  };
  
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <Body className="text-gray-600">
              Select the type of report you want to generate.
            </Body>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {reportTypes.map((type) => (
                <Card
                  key={type.id}
                  className={`p-4 cursor-pointer transition-all ${
                    config.type === type.id
                      ? 'ring-2 ring-primary-500 border-primary-200'
                      : 'hover:shadow-md'
                  }`}
                  onClick={() => updateConfig({ type: type.id as ReportConfig['type'] })}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${type.color} text-white`}>
                      <type.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <H3 className="font-medium">{type.title}</H3>
                      <Body className="text-sm text-gray-600 mt-1">
                        {type.description}
                      </Body>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-6">
            <Body className="text-gray-600">
              Select the specific content for your report.
            </Body>
            
            {config.type === 'individual' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Child <span className="text-red-500">*</span>
                </label>
                <Select
                  value={config.childId || ''}
                  onChange={(e) => updateConfig({ childId: e.target.value })}
                  options={[
                    { value: '', label: 'Select a child' },
                    ...children.map(child => ({
                      value: child.id,
                      label: `${child.name} ${child.surname}`,
                    }))
                  ]}
                />
              </div>
            )}
            
            {config.type === 'classroom' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Classroom <span className="text-red-500">*</span>
                </label>
                <Select
                  value={config.classroomId || ''}
                  onChange={(e) => updateConfig({ classroomId: e.target.value })}
                  options={[
                    { value: '', label: 'Select a classroom' },
                    ...classrooms.map(classroom => ({
                      value: classroom.id,
                      label: classroom.name,
                    }))
                  ]}
                />
              </div>
            )}
            
            {config.type === 'assessment-matrix' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Learning Areas <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {learningAreas?.map(area => (
                    <div key={area.id} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`area-${area.id}`}
                        checked={config.learningAreaIds?.includes(area.id) || false}
                        onChange={(e) => {
                          const current = config.learningAreaIds || [];
                          if (e.target.checked) {
                            updateConfig({ learningAreaIds: [...current, area.id] });
                          } else {
                            updateConfig({ learningAreaIds: current.filter(id => id !== area.id) });
                          }
                        }}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`area-${area.id}`} className="ml-2 block text-sm text-gray-900">
                        {area.name} ({area.code})
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {config.type === 'analytics' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Metrics <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {availableMetrics.map(metric => (
                    <div key={metric} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`metric-${metric}`}
                        checked={config.metrics?.includes(metric) || false}
                        onChange={(e) => {
                          const current = config.metrics || [];
                          if (e.target.checked) {
                            updateConfig({ metrics: [...current, metric] });
                          } else {
                            updateConfig({ metrics: current.filter(m => m !== metric) });
                          }
                        }}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`metric-${metric}`} className="ml-2 block text-sm text-gray-900">
                        {metric}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-6">
            <Body className="text-gray-600">
              Set the date range for your report data.
            </Body>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CalendarDaysIcon className="w-4 h-4 inline mr-1" />
                  Start Date <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={config.dateRange.start}
                  onChange={(e) => updateConfig({ 
                    dateRange: { ...config.dateRange, start: e.target.value } 
                  })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CalendarDaysIcon className="w-4 h-4 inline mr-1" />
                  End Date <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={config.dateRange.end}
                  onChange={(e) => updateConfig({ 
                    dateRange: { ...config.dateRange, end: e.target.value } 
                  })}
                />
              </div>
            </div>
            
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Body className="text-blue-800 text-sm">
                <strong>Note:</strong> The report will include all observations and assessments 
                within the selected date range.
              </Body>
            </div>
          </div>
        );
      
      case 4:
        return (
          <div className="space-y-6">
            <Body className="text-gray-600">
              Configure your report options and provide a title.
            </Body>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Title <span className="text-red-500">*</span>
              </label>
              <Input
                value={config.title}
                onChange={(e) => updateConfig({ title: e.target.value })}
                placeholder="Enter a descriptive title for your report"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <TextArea
                value={config.description}
                onChange={(e) => updateConfig({ description: e.target.value })}
                placeholder="Add any additional notes or context for this report"
                rows={3}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Include in Report
              </label>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="include-observations"
                    checked={config.includeObservations}
                    onChange={(e) => updateConfig({ includeObservations: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="include-observations" className="ml-2 block text-sm text-gray-900">
                    Include Observations
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="include-assessments"
                    checked={config.includeAssessments}
                    onChange={(e) => updateConfig({ includeAssessments: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="include-assessments" className="ml-2 block text-sm text-gray-900">
                    Include Assessments
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="include-media"
                    checked={config.includeMedia}
                    onChange={(e) => updateConfig({ includeMedia: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="include-media" className="ml-2 block text-sm text-gray-900">
                    Include Media Attachments
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="include-recommendations"
                    checked={config.includeRecommendations}
                    onChange={(e) => updateConfig({ includeRecommendations: e.target.checked })}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="include-recommendations" className="ml-2 block text-sm text-gray-900">
                    Include Recommendations
                  </label>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Output Format
              </label>
              <Select
                value={config.format}
                onChange={(e) => updateConfig({ format: e.target.value as ReportConfig['format'] })}
                options={[
                  { value: 'pdf', label: 'PDF Document' },
                  { value: 'docx', label: 'Word Document' },
                  { value: 'online', label: 'Online View' },
                ]}
              />
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      showCloseButton={!isGenerating}
      closeOnEscape={!isGenerating}
      closeOnOverlayClick={!isGenerating}
    >
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <H1 className="mb-2">Create Report</H1>
          <Body className="text-gray-600">
            Follow the steps to generate your custom report.
          </Body>
        </div>
        
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step <= currentStep 
                    ? 'bg-primary-600 text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {step < currentStep ? (
                    <CheckCircleIcon className="w-4 h-4" />
                  ) : (
                    step
                  )}
                </div>
                {step < 4 && (
                  <div className={`w-16 h-1 mx-2 ${
                    step < currentStep ? 'bg-primary-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="mt-2">
            <Body className="text-sm text-gray-600">
              Step {currentStep} of 4: {getStepTitle()}
            </Body>
          </div>
        </div>
        
        {/* Step Content */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-8"
        >
          {renderStep()}
        </motion.div>
        
        {/* Navigation */}
        <div className="flex items-center justify-between">
          <div>
            {currentStep > 1 && (
              <Button
                variant="ghost"
                onClick={handlePrevious}
                disabled={isGenerating}
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Previous
              </Button>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isGenerating}
            >
              Cancel
            </Button>
            
            {currentStep < 4 ? (
              <Button
                variant="primary"
                onClick={handleNext}
                disabled={!isStepValid() || isGenerating}
              >
                Next
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleGenerate}
                disabled={!isStepValid() || isGenerating}
                isLoading={isGenerating}
              >
                {isGenerating ? 'Generating...' : 'Generate Report'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ReportWizard;
